import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, GameController, Plus, Clock, Cube, House, RoadHorizon, Sword, ArrowsCounterClockwise } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_ICONS = {
  dice_roll: Cube, road_built: RoadHorizon, settlement_built: House, city_upgraded: House,
  dev_card_bought: Sword, dev_card_played: Sword, robber_moved: ArrowsCounterClockwise,
  trade_made: ArrowsCounterClockwise, resource_collected: GameController, victory_point: GameController,
  custom_event: GameController, score_change: GameController, turn_end: Clock,
};

export default function LiveTrackerPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [gameState, setGameState] = useState(null);
  const [turnNumber, setTurnNumber] = useState(1);
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('controls');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchEvents, 5000);
    return () => clearInterval(interval);
  }, [sessionId]);

  const fetchData = async () => {
    try {
      const [sessRes, typeRes, eventsRes, stateRes] = await Promise.all([
        axios.get(`${API_URL}/api/sessions/${sessionId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/sessions/${sessionId}/event-types`, { withCredentials: true }),
        axios.get(`${API_URL}/api/sessions/${sessionId}/events`, { withCredentials: true }),
        axios.get(`${API_URL}/api/sessions/${sessionId}/game-state`, { withCredentials: true }),
      ]);
      setSession(sessRes.data);
      setEventTypes(typeRes.data.event_types || []);
      setEvents(eventsRes.data);
      setGameState(stateRes.data);
      if (sessRes.data.participants?.length > 0) setSelectedPlayer(sessRes.data.participants[0].id);
    } catch (err) { toast.error('Failed to load session'); }
    finally { setLoading(false); }
  };

  const fetchEvents = async () => {
    try {
      const [eventsRes, stateRes] = await Promise.all([
        axios.get(`${API_URL}/api/sessions/${sessionId}/events`, { withCredentials: true }),
        axios.get(`${API_URL}/api/sessions/${sessionId}/game-state`, { withCredentials: true }),
      ]);
      setEvents(eventsRes.data);
      setGameState(stateRes.data);
    } catch (err) { /* silent */ }
  };

  const addEvent = async (eventType, extraData = {}) => {
    try {
      await axios.post(`${API_URL}/api/sessions/${sessionId}/events`, {
        event_type: eventType,
        player_id: selectedPlayer || user?.id,
        turn_number: turnNumber,
        event_data: extraData,
      }, { withCredentials: true });
      fetchEvents();
      toast.success('Event recorded');
    } catch (err) { toast.error('Failed to record event'); }
  };

  const handleDiceRoll = () => {
    const d1 = Math.floor(Math.random() * 6) + 1;
    const d2 = Math.floor(Math.random() * 6) + 1;
    addEvent('dice_roll', { value: d1 + d2, dice: [d1, d2] });
  };

  if (loading) return <div className="app-container flex items-center justify-center min-h-screen"><div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="app-container" data-testid="live-tracker-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-white truncate" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Live Tracker
            </h1>
            <p className="text-[#71717A] text-xs truncate">{session?.title} - {session?.game_type}</p>
          </div>
          <div className="text-right">
            <p className="text-[#00F0FF] text-xs font-bold">Turn {turnNumber}</p>
            <p className="text-[#71717A] text-[10px]">{events.length} events</p>
          </div>
        </div>

        {/* Player Selector + Turn */}
        <div className="flex gap-2 mb-3">
          <select value={selectedPlayer} onChange={e => setSelectedPlayer(e.target.value)}
            className="flex-1 input-field text-sm" data-testid="player-select">
            {session?.participants?.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <div className="flex items-center gap-1">
            <button onClick={() => setTurnNumber(Math.max(1, turnNumber - 1))} className="w-8 h-10 rounded-lg bg-[#181824] text-white text-sm">-</button>
            <span className="text-white text-sm w-6 text-center">{turnNumber}</span>
            <button onClick={() => setTurnNumber(turnNumber + 1)} className="w-8 h-10 rounded-lg bg-[#181824] text-white text-sm" data-testid="next-turn">+</button>
          </div>
        </div>

        <div className="flex gap-2">
          {['controls', 'timeline', 'stats'].map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all ${activeTab === t ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white' : 'bg-[#181824] text-[#A1A1AA]'}`}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </header>

      <div className="px-6 pb-32">
        {activeTab === 'controls' && (
          <div className="space-y-3">
            {/* Dice Roll */}
            <button onClick={handleDiceRoll} className="w-full p-4 rounded-xl bg-gradient-to-br from-[#9D4CDD]/20 to-[#7C3AED]/10 border border-[#9D4CDD]/30 text-white flex items-center gap-3 hover:border-[#9D4CDD] transition-all" data-testid="dice-roll-btn">
              <Cube size={28} weight="duotone" className="text-[#9D4CDD]" />
              <div className="text-left"><p className="font-medium text-sm">Roll Dice</p><p className="text-[#71717A] text-[10px]">Random 2d6 roll</p></div>
            </button>

            {/* Action Buttons Grid */}
            <div className="grid grid-cols-2 gap-2">
              {eventTypes.filter(et => et.type !== 'dice_roll').map(et => {
                const Icon = EVENT_ICONS[et.type] || GameController;
                return (
                  <button key={et.type} onClick={() => addEvent(et.type)}
                    className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 text-left hover:border-[#00F0FF]/30 transition-all" data-testid={`event-btn-${et.type}`}>
                    <Icon size={20} weight="duotone" className="text-[#00F0FF] mb-1" />
                    <p className="text-white text-xs font-medium">{et.label}</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="space-y-2">
            {events.length === 0 ? (
              <p className="text-center text-[#71717A] py-8 text-sm">No events yet. Start tracking!</p>
            ) : (
              [...events].reverse().map((e, i) => {
                const Icon = EVENT_ICONS[e.event_type] || GameController;
                return (
                  <motion.div key={e.id || i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                    className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#181824] flex items-center justify-center shrink-0">
                      <Icon size={16} weight="duotone" className="text-[#00F0FF]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-medium">{e.event_type.replace(/_/g, ' ')}</p>
                      <p className="text-[#71717A] text-[10px]">{e.player_name || 'Unknown'} - Turn {e.turn_number}</p>
                    </div>
                    {e.event_data?.value && <span className="text-[#FACC15] font-bold text-sm">{e.event_data.value}</span>}
                  </motion.div>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'stats' && gameState && (
          <div className="space-y-3">
            <div className="p-3 rounded-xl bg-[#0F0F16] border border-white/5">
              <p className="text-[#A1A1AA] text-xs mb-2">Game Summary</p>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div><p className="text-white font-bold">{gameState.total_events}</p><p className="text-[#71717A] text-[10px]">Events</p></div>
                <div><p className="text-white font-bold">{gameState.total_turns}</p><p className="text-[#71717A] text-[10px]">Turns</p></div>
              </div>
            </div>

            {Object.entries(gameState.player_stats || {}).map(([pid, stats]) => (
              <div key={pid} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5">
                <p className="text-white text-sm font-medium mb-2">{stats.name}</p>
                <div className="grid grid-cols-4 gap-1 text-center text-[10px]">
                  <div><p className="text-[#00F0FF] font-bold">{stats.roads}</p><p className="text-[#71717A]">Roads</p></div>
                  <div><p className="text-[#39FF14] font-bold">{stats.settlements}</p><p className="text-[#71717A]">Settle</p></div>
                  <div><p className="text-[#FACC15] font-bold">{stats.cities}</p><p className="text-[#71717A]">Cities</p></div>
                  <div><p className="text-[#9D4CDD] font-bold">{stats.dev_cards}</p><p className="text-[#71717A]">Dev</p></div>
                </div>
              </div>
            ))}

            {gameState.dice_distribution && Object.keys(gameState.dice_distribution).length > 0 && (
              <div className="p-3 rounded-xl bg-[#0F0F16] border border-white/5">
                <p className="text-[#A1A1AA] text-xs mb-2">Dice Distribution</p>
                <div className="flex items-end gap-1 h-16">
                  {[2,3,4,5,6,7,8,9,10,11,12].map(n => {
                    const count = parseInt(gameState.dice_distribution[String(n)] || 0);
                    const maxCount = Math.max(...Object.values(gameState.dice_distribution).map(Number), 1);
                    const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                    return (
                      <div key={n} className="flex-1 flex flex-col items-center">
                        <div className="w-full rounded-t-sm bg-gradient-to-t from-[#9D4CDD] to-[#00F0FF]" style={{ height: `${Math.max(height, 4)}%` }} />
                        <span className="text-[8px] text-[#71717A] mt-0.5">{n}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
